/**
 * RoutesTab Component
 * Displays multi-destination route suggestions
 */
import React, { useEffect, useState } from 'react';
import { Row, Col, Spinner, Badge, Form } from 'react-bootstrap';
import { MapPin } from 'lucide-react';
import { discoveryService } from '../../../services/api/discoveryService';
import { RouteSuggestion } from '../../../types/discovery.types';
import { DiscoveryTripCard } from '../DiscoveryTripCard';
import './DiscoveryTab.css';

export const RoutesTab: React.FC = () => {
  const [routes, setRoutes] = useState<RouteSuggestion[]>([]);
  const [destinations, setDestinations] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    loadPopularRoutes();
  }, []);

  const loadPopularRoutes = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await discoveryService.getPopularRoutes({ limit: 12 });
      setRoutes(data.routes);
      setHasSearched(false);
    } catch (err) {
      setError('Failed to load popular routes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!destinations.trim()) {
      loadPopularRoutes();
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const destinationList = destinations
        .split(',')
        .map((d) => d.trim())
        .filter((d) => d.length > 0);

      const data = await discoveryService.suggestRoutes(
        destinationList[0],
        destinationList[destinationList.length - 1],
        {
          limit: 12
        }
      );
      setRoutes(data.routes);
    } catch (err) {
      setError('Failed to find routes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderRouteInfo = (route: RouteSuggestion): React.ReactNode => {
    return (
      <div className='discovery-route-info'>
        <div className='discovery-route-path'>
          {route.route.map((destination, index) => (
            <React.Fragment key={index}>
              <span className='discovery-route-destination'>{destination}</span>
              {index < route.route.length - 1 && (
                <span className='discovery-route-arrow'>→</span>
              )}
            </React.Fragment>
          ))}
        </div>
        {route.intermediateStops.length > 0 && (
          <div className='discovery-route-stops'>
            <MapPin size={14} />
            <span>{route.intermediateStops.length} stops along the way</span>
          </div>
        )}
      </div>
    );
  };

  if (loading && routes.length === 0) {
    return (
      <div className='discovery-tab-loading'>
        <Spinner
          animation='border'
          variant='primary'
        />
        <p>Finding the best routes...</p>
      </div>
    );
  }

  return (
    <div className='discovery-tab'>
      <div className='discovery-tab-header'>
        <h2>Multi-Destination Routes</h2>
        <p>Discover trips that connect multiple amazing destinations</p>
      </div>

      <div className='discovery-routes-search'>
        <Form onSubmit={handleSearch}>
          <Form.Group>
            <Form.Label>Search by destinations (comma-separated)</Form.Label>
            <div className='discovery-routes-search-input'>
              <Form.Control
                type='text'
                placeholder='e.g., Paris, Rome, Barcelona'
                value={destinations}
                onChange={(e) => setDestinations(e.target.value)}
              />
              <button
                type='submit'
                className='btn btn-primary'
                disabled={loading}
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
              {hasSearched && (
                <button
                  type='button'
                  className='btn btn-outline-secondary'
                  onClick={loadPopularRoutes}
                >
                  Show Popular
                </button>
              )}
            </div>
          </Form.Group>
        </Form>
      </div>

      {error && (
        <div
          className='alert alert-danger'
          role='alert'
        >
          {error}
        </div>
      )}

      {routes.length === 0 ? (
        <div className='discovery-tab-empty'>
          <h3>No routes found</h3>
          <p>
            {hasSearched
              ? 'Try different destinations or check popular routes'
              : 'Start by searching for destinations'}
          </p>
        </div>
      ) : (
        <Row
          xs={1}
          md={2}
          lg={3}
          className='g-4'
        >
          {routes.map((route) => (
            <Col key={route.trip._id}>
              <DiscoveryTripCard
                trip={route.trip}
                reason={undefined}
                badge={
                  route.popularity > 50 ? (
                    <Badge
                      bg='warning'
                      text='dark'
                    >
                      Popular Route
                    </Badge>
                  ) : null
                }
              />
              {renderRouteInfo(route)}
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};
